import { fetchProductsFromCollectionTitle, parseShortDescription } from './shopifyApi';
import { productUnavailableReason, DEFAULT_UNAVAILABLE_MESSAGE, type ProductLabelFields } from './productLabels';

export const KATEGORIE_DODATKI = [
  {
    id: 'wazony',
    name: 'Wazony',
    description: 'Szklane wazony do bukietu',
    collectionTitle: 'Wazony',
  },
  {
    id: 'pluszaki',
    name: 'Pluszaki',
    description: 'Mały gest, który zostaje na dłużej',
    collectionTitle: 'Pluszaki',
  },
  {
    id: 'czekoladki',
    name: 'Czekoladki',
    description: 'Merci, Raffaello i więcej',
    collectionTitle: 'Czekoladki',
  },
  {
    id: 'toppery',
    name: 'Toppery',
    description: 'Dodaj osobisty akcent',
    collectionTitle: 'Toppery',
  },
  {
    id: 'alkohole',
    name: 'Alkohole',
    description: 'Wina, szampany i nie tylko',
    collectionTitle: 'Alkohole',
  },
] as const;

export type CategoryId = typeof KATEGORIE_DODATKI[number]['id'];

export interface DodatekResolved extends ProductLabelFields {
  id: string;
  name: string;
  price: number;
  description: string | null;
  imageUrl: string | null;
  categoryId: CategoryId;
  shopifyVariantId: string | null;
  isBestseller: boolean;
}

export function initialAddonsByCategory(): Record<string, DodatekResolved[]> {
  // A placeholder has no Shopify identity, so its stock and labels cannot be checked.
  return Object.fromEntries(KATEGORIE_DODATKI.map(category => [category.id, []]));
}

let catalogPromise: Promise<Record<string, DodatekResolved[]>> | null = null;

/** One catalog source for product pages, creators, and the add-to-cart panel. */
export function fetchAddonCatalog(): Promise<Record<string, DodatekResolved[]>> {
  if (!catalogPromise) {
    catalogPromise = Promise.all(KATEGORIE_DODATKI.map(async category => {
      let resolved: DodatekResolved[] = [];
      try {
        const products = await fetchProductsFromCollectionTitle(category.collectionTitle);
        resolved = products.map(product => {
          const variant = product.variants.nodes.find(candidate => candidate.availableForSale) ?? product.variants.nodes[0] ?? null;
          const unavailableMessage = productUnavailableReason(product, variant)
            || (!variant ? DEFAULT_UNAVAILABLE_MESSAGE : null);
          const description = (product.shortDescription?.value
            ? parseShortDescription(product.shortDescription.value)
            : null)
            ?? product.description?.replace(/<[^>]*>/g, '').trim()
            ?? null;
          return {
            id: product.handle,
            labels: product.labels,
            purchaseBlocked: Boolean(unavailableMessage),
            unavailableMessage,
            name: product.title,
            price: variant ? parseFloat(variant.price.amount) : 0,
            description: description || null,
            imageUrl: product.featuredImage?.url ?? product.images?.nodes?.[0]?.url ?? null,
            categoryId: category.id as CategoryId,
            shopifyVariantId: variant?.id ?? null,
            isBestseller: product.labels?.some(label => label.name.trim().toLowerCase() === 'bestseller')
              || (product.tags?.some(tag => tag.trim().toLowerCase() === 'bestseller') ?? false),
          };
        });
      } catch {
        // Never substitute an unverified, attribute-priced product after an outage.
      }
      return [category.id, resolved] as const;
    })).then(entries => Object.fromEntries(entries));
  }
  return catalogPromise;
}
